// The `requestAnimationFrame` render loop (design D8, UI.md §8) over the group-1.1
// injectable `FrameScheduler` + `Clock` seams. It reschedules itself once per frame and
// invokes the frame callback with the injected clock's `now()`, so the whole thing — the
// hover-bob wall-clock, the delayed interpolation target, and the loop lifecycle — is driven
// off the two injected seams and is deterministically testable without a real RAF or a GPU.
//
// `init` / `destroy` are idempotent: a second `init` while running is a no-op (exactly one
// loop is ever scheduled — never a duplicate RAF chain), and `destroy` cancels the pending
// frame so no further frames run, a second `destroy` being a harmless no-op. The frame
// callback reads the LATEST render-input each frame (it is a closure over the caller's live
// selectors, not a value captured at `init`), so an independent (non-world) lifecycle /
// season / streamEpoch change reaches the draw body within one frame even while the stream
// stalls.
//
// Pure of any Pixi/GPU dependency — the CanvasRenderer (group 11) composes this with the
// presentation clock and the draw body; that GPU wiring is exercised on-device only (D-086).

import type { Clock, FrameScheduler } from './frame-scheduler';

export class RenderLoop {
  private handle: number | null = null;
  private frameCallback: ((nowMs: number) => void) | null = null;
  private active = false;

  constructor(
    private readonly scheduler: FrameScheduler,
    private readonly clock: Clock,
  ) {}

  /** Whether the loop is currently scheduling frames. */
  get isRunning(): boolean {
    return this.active;
  }

  /**
   * Start the loop, scheduling the first frame through the injected scheduler. Idempotent:
   * called again while already running it is a no-op, so no matter how many times a mount
   * effect fires there is only ever ONE scheduled loop (no duplicate RAF chains).
   */
  init(onFrame: (nowMs: number) => void): void {
    if (this.active) {
      return; // already running — never schedule a second concurrent loop
    }
    this.active = true;
    this.frameCallback = onFrame;
    this.scheduleNext();
  }

  /**
   * Stop the loop and cancel any pending frame, so no further callbacks run. Idempotent — a
   * second `destroy` (or a `destroy` before `init`) is a harmless no-op.
   */
  destroy(): void {
    this.active = false;
    if (this.handle !== null) {
      this.scheduler.cancel(this.handle);
      this.handle = null;
    }
    this.frameCallback = null;
  }

  private scheduleNext(): void {
    this.handle = this.scheduler.schedule(() => this.runFrame());
  }

  private runFrame(): void {
    if (!this.active) {
      return; // cancelled between scheduling and running — do nothing
    }
    this.frameCallback?.(this.clock.now());
    // Reschedule only if still running (the callback may have torn the loop down).
    if (this.active) {
      this.scheduleNext();
    }
  }
}
